/**
 * Hooks de listado + CRUD de tareas.
 * Cubre: lista filtrable, lista por prospecto, detalle por id, create/update/delete.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import type { TasksInsert, TasksRow, TasksUpdate } from '@/types/database.types';

import * as tasksApi from '../api/tasks';
import { TASK_KEYS } from './keys';

export function useTasks(filters: tasksApi.ListTasksFilters = {}) {
  return useQuery({
    queryKey: TASK_KEYS.list(filters),
    queryFn: () => tasksApi.listTasks(filters),
    staleTime: 30_000,
  });
}

export function useTasksByProspect(prospectId: string | null | undefined) {
  return useQuery({
    queryKey: TASK_KEYS.byProspect(prospectId ?? ''),
    queryFn: () => tasksApi.listTasks({ prospectId: prospectId ?? undefined }),
    enabled: !!prospectId,
    staleTime: 30_000,
  });
}

export function useTask(id: string | null | undefined) {
  return useQuery({
    queryKey: TASK_KEYS.detail(id ?? ''),
    queryFn: () => tasksApi.getTask(id!),
    enabled: !!id,
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TasksInsert) => tasksApi.createTask(input),
    onSuccess: (task) => {
      qc.invalidateQueries({ queryKey: ['core', 'tasks'] });
      if (task.prospect_id) {
        qc.invalidateQueries({ queryKey: TASK_KEYS.byProspect(task.prospect_id) });
      }
      qc.invalidateQueries({ queryKey: TASK_KEYS.myDue });
      toast.success('Tarea creada');
    },
    onError: (error: Error) =>
      toast.error('No se pudo crear la tarea', { description: error.message }),
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TasksUpdate }) =>
      tasksApi.updateTask(id, patch),
    onSuccess: (task) => {
      qc.invalidateQueries({ queryKey: ['core', 'tasks'] });
      qc.invalidateQueries({ queryKey: TASK_KEYS.detail(task.id) });
      qc.invalidateQueries({ queryKey: TASK_KEYS.myDue });
    },
    onError: (error: Error) =>
      toast.error('No se pudo actualizar', { description: error.message }),
  });
}

/**
 * Mover una tarea a otra fecha/hora desde el calendario (drag & drop).
 * Update OPTIMISTA sobre las caches del calendario (`['core','tasks','calendar']`)
 * para que el chip salte al instante; `updateTask` además re-sincroniza a Google.
 * Si la mutación falla, revierte y avisa.
 */
export function useMoveTaskDueAt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dueAt }: { id: string; dueAt: string }) =>
      tasksApi.updateTask(id, { due_at: dueAt }),
    onMutate: async ({ id, dueAt }) => {
      await qc.cancelQueries({ queryKey: ['core', 'tasks', 'calendar'] });
      const prev = qc.getQueriesData<TasksRow[]>({ queryKey: ['core', 'tasks', 'calendar'] });
      for (const [key, data] of prev) {
        if (!data) continue;
        qc.setQueryData(
          key,
          data.map((t) => (t.id === id ? { ...t, due_at: dueAt } : t)),
        );
      }
      return { prev };
    },
    onError: (error: Error, _vars, ctx) => {
      ctx?.prev?.forEach(([key, data]) => qc.setQueryData(key, data));
      toast.error('No se pudo mover la tarea', { description: error.message });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['core', 'tasks'] });
      qc.invalidateQueries({ queryKey: TASK_KEYS.myDue });
    },
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tasksApi.softDeleteTask(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['core', 'tasks'] });
      qc.invalidateQueries({ queryKey: TASK_KEYS.myDue });
      toast.success('Tarea eliminada');
    },
    onError: (error: Error) =>
      toast.error('No se pudo eliminar', { description: error.message }),
  });
}
